import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { synthwave84 } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { StanDataRow, StanDataRowGroup } from '@/components';
import { StanFloatingMenu } from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { FaChartLine, FaEnvelope, FaUser, FaVideo } from 'react-icons/fa';
import { FiEdit, FiEye, FiMoreVertical, FiShare, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';

export default function InternalsDataRowPage() {
    const [activeBasic, setActiveBasic] = useState('preview');
    const [activeEmailFlows, setActiveEmailFlows] = useState('preview');
    const [activeProducts, setActiveProducts] = useState('preview');

    const CodeBlock = ({ code }: { code: string }) => (
        <div className='border-muted overflow-hidden rounded-md border'>
            <div className='text-body-md [&_code]:w-full! [&_code]:!p-0 [&_code]:!text-base [&_code]:!leading-relaxed [&_pre]:!m-0 [&_pre]:!p-6'>
                <SyntaxHighlighter language='typescript' style={synthwave84} showLineNumbers={false}>
                    {code.trim()}
                </SyntaxHighlighter>
            </div>
        </div>
    );

    return (
        <div className='mx-auto max-w-6xl space-y-12 p-6 pb-40'>
            {/* Page Header */}
            <div className='space-y-2'>
                <h1 className='text-heading-h1 text-dark'>StanDataRow</h1>
                <p className='text-body-lg text-gray'>
                    A flexible data row component for displaying structured information with actions
                </p>
            </div>

            {/* Basic Usage */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Basic Usage</h2>
                    <p className='text-body-md text-gray mt-2'>Simple data row with icon, title, status, and actions</p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveBasic('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeBasic === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveBasic('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeBasic === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeBasic === 'preview' ? (
                            <StanDataRowGroup
                                headerColumns={[
                                    { name: 'Item', width: 40 },
                                    { name: 'Status', width: 30 },
                                    { name: 'Category', width: 30 },
                                ]}
                                maxActionButtonCount={1}
                            >
                                <StanDataRow
                                    clickable
                                    onClick={() => toast.info('Data row clicked!')}
                                    columns={[
                                        {
                                            name: 'Item',
                                            icon: FaUser,
                                            value: (
                                                <div>
                                                    <div className='text-label-md-medium text-dark'>Sample Item</div>
                                                    <div className='text-label-sm-regular text-gray'>
                                                        This is a subtitle
                                                    </div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Status',
                                            type: 'pill',
                                            value: 'Active',
                                            statusVariant: 'active',
                                        },
                                        {
                                            name: 'Category',
                                            value: (
                                                <div className='bg-neutral-subtle text-dark inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium'>
                                                    Marketing
                                                </div>
                                            ),
                                        },
                                    ]}
                                    actionButtons={[
                                        {
                                            id: 'view-action',
                                            type: 'button',
                                            icon: FiEye,
                                            label: 'View',
                                            onClick: () => toast.info('View clicked'),
                                        },
                                    ]}
                                />
                            </StanDataRowGroup>
                        ) : (
                            <CodeBlock
                                code={`import { StanDataRow, StanDataRowGroup } from '@/components';
import { FaUser } from 'react-icons/fa';
import { FiEye } from 'react-icons/fi';

export const BasicExample = () => {
  return (
    <StanDataRowGroup
      headerColumns={[
        { name: 'Item', width: 40 },
        { name: 'Status', width: 30 },
        { name: 'Category', width: 30 },
      ]}
      maxActionButtonCount={1}
    >
      <StanDataRow
        clickable
        onClick={() => handleClick()}
        columns={[
          {
            name: 'Item',
            icon: FaUser,
            value: (
              <div>
                <div className="font-semibold">Sample Item</div>
                <div className="text-sm text-gray-500">This is a subtitle</div>
              </div>
            ),
          },
          {
            name: 'Status',
            type: 'pill',
            value: 'Active',
            statusVariant: 'active',
          },
          {
            name: 'Category',
            value: (
              <div className="inline-flex items-center rounded-full bg-neutral-subtle px-2.5 py-0.5 text-xs font-medium">
                Marketing
              </div>
            ),
          },
        ]}
        actionButtons={[
          {
            id: 'view-action',
            type: 'button',
            icon: FiEye,
            label: 'View',
            onClick: () => handleView(),
          },
        ]}
      />
    </StanDataRowGroup>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* Email Flows Example */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Email Flow Management</h2>
                    <p className='text-body-md text-gray mt-2'>
                        Email automation campaigns with performance tracking and actions
                    </p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveEmailFlows('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeEmailFlows === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveEmailFlows('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeEmailFlows === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeEmailFlows === 'preview' ? (
                            <StanDataRowGroup
                                headerColumns={[
                                    { name: 'Campaign', width: 30 },
                                    { name: 'Performance', width: 25 },
                                    { name: 'Status', width: 25 },
                                    { name: 'Revenue', width: 20 },
                                ]}
                                maxActionButtonCount={2}
                                className='space-y-4'
                            >
                                <StanDataRow
                                    clickable
                                    onClick={() => toast.info('Opening Welcome Series details')}
                                    columns={[
                                        {
                                            name: 'Campaign',
                                            icon: FaEnvelope,
                                            value: (
                                                <div>
                                                    <div className='text-label-md-medium text-dark'>Welcome Series</div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Performance',
                                            value: (
                                                <div className='space-y-1'>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Sent:</span> 15,420
                                                    </div>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Open Rate:</span> 24.5%
                                                    </div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Status',
                                            type: 'pill',
                                            value: 'Active',
                                            statusVariant: 'active',
                                        },
                                        {
                                            name: 'Revenue',
                                            value: (
                                                <div>
                                                    <div className='text-label-md-medium text-success'>CA$12,470</div>
                                                    <div className='text-label-xs-regular text-gray'>Total earned</div>
                                                </div>
                                            ),
                                        },
                                    ]}
                                    actionButtons={[
                                        {
                                            id: 'analytics-welcome',
                                            type: 'button',
                                            icon: FaChartLine,
                                            label: 'View Analytics',
                                            onClick: () => toast.info('Opening analytics'),
                                        },
                                        {
                                            id: 'flow-actions-welcome',
                                            type: 'dropdown',
                                            label: 'More Actions',
                                            onClick: () => {},
                                            menu: (
                                                <StanFloatingMenu
                                                    name='flow-menu-welcome'
                                                    placement='bottom-end'
                                                    trigger={
                                                        <button className='hover:bg-neutral-subtle flex size-8 cursor-pointer items-center justify-center rounded-md'>
                                                            <FiMoreVertical className='text-gray size-5' />
                                                        </button>
                                                    }
                                                >
                                                    {({ closeMenu }) => (
                                                        <div className='min-w-[140px] py-1'>
                                                            <button
                                                                className='hover:bg-neutral-subtle text-dark flex w-full items-center gap-2 px-4 py-2 text-sm'
                                                                onClick={() => {
                                                                    toast.info('Editing flow');
                                                                    closeMenu();
                                                                }}
                                                            >
                                                                <FiEdit className='size-4' />
                                                                Edit Flow
                                                            </button>
                                                            <button
                                                                className='hover:bg-neutral-subtle text-dark flex w-full items-center gap-2 px-4 py-2 text-sm'
                                                                onClick={() => {
                                                                    toast.info('Pausing flow');
                                                                    closeMenu();
                                                                }}
                                                            >
                                                                <FiShare className='size-4' />
                                                                Pause Flow
                                                            </button>
                                                            <button
                                                                className='flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50'
                                                                onClick={() => {
                                                                    toast.error('Deleting flow');
                                                                    closeMenu();
                                                                }}
                                                            >
                                                                <FiTrash2 className='size-4' />
                                                                Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </StanFloatingMenu>
                                            ),
                                        },
                                    ]}
                                />

                                <StanDataRow
                                    columns={[
                                        {
                                            name: 'Campaign',
                                            icon: FaEnvelope,
                                            value: (
                                                <div>
                                                    <div className='text-label-md-medium text-dark'>Product Launch</div>
                                                    <div className='text-label-sm-regular text-gray'>
                                                        New course announcement
                                                    </div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Performance',
                                            value: (
                                                <div className='space-y-1'>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Sent:</span> 8,930
                                                    </div>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Open Rate:</span> 32.1%
                                                    </div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Status',
                                            type: 'pill',
                                            value: 'Scheduled',
                                            statusVariant: 'scheduled',
                                        },
                                        {
                                            name: 'Revenue',
                                            value: (
                                                <div>
                                                    <div className='text-label-md-medium text-success'>CA$18,650</div>
                                                    <div className='text-label-xs-regular text-gray'>Total earned</div>
                                                </div>
                                            ),
                                        },
                                    ]}
                                />
                            </StanDataRowGroup>
                        ) : (
                            <CodeBlock
                                code={`import { StanDataRow, StanDataRowGroup } from '@/components';
import { FaEnvelope, FaChartLine } from 'react-icons/fa';
import { FiMoreVertical, FiEdit, FiShare, FiTrash2 } from 'react-icons/fi';

export const EmailFlowsExample = () => {
  return (
    <StanDataRowGroup
      headerColumns={[
        { name: 'Campaign', width: 30 },
        { name: 'Performance', width: 25 },
        { name: 'Status', width: 25 },
        { name: 'Revenue', width: 20 },
      ]}
      maxActionButtonCount={2}
    >
      <StanDataRow
        clickable
        onClick={() => handleClick()}
        columns={[
          {
            name: 'Campaign',
            icon: FaEnvelope,
            value: (
              <div>
                <div className="font-semibold">Welcome Series</div>
              </div>
            ),
          },
          {
            name: 'Performance',
            value: (
              <div className="space-y-1">
                <div className="text-sm">
                  <span className="text-gray-600">Sent:</span> 15,420
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">Open Rate:</span> 24.5%
                </div>
              </div>
            ),
          },
          {
            name: 'Status',
            type: 'pill',
            value: 'Active',
            statusVariant: 'active',
          },
          {
            name: 'Revenue',
            value: (
              <div>
                <div className="font-bold text-green-600">CA$12,470</div>
                <div className="text-xs text-gray-500">Total earned</div>
              </div>
            ),
          },
        ]}
        actionButtons={[
          {
            id: 'analytics',
            type: 'button',
            icon: FaChartLine,
            label: 'View Analytics',
            onClick: () => handleAnalytics(),
          },
          {
            id: 'actions',
            type: 'dropdown',
            label: 'More Actions',
            menu: <DropdownMenu />,
          },
        ]}
      />
    </StanDataRowGroup>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* Products Example */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Product Management</h2>
                    <p className='text-body-md text-gray mt-2'>Digital products with sales tracking and store links</p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveProducts('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeProducts === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveProducts('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeProducts === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeProducts === 'preview' ? (
                            <StanDataRowGroup
                                headerColumns={[
                                    { name: 'Product', width: 30 },
                                    { name: 'Sales Data', width: 20 },
                                    { name: 'Performance', width: 20 },
                                    { name: 'Status', width: 15 },
                                    { name: 'Store URL', width: 15 },
                                ]}
                                maxActionButtonCount={1}
                                className='space-y-4'
                            >
                                <StanDataRow
                                    clickable
                                    onClick={() => toast.info('Opening product details')}
                                    columns={[
                                        {
                                            name: 'Product',
                                            icon: FaVideo,
                                            value: (
                                                <div>
                                                    <div className='text-label-md-medium text-dark'>
                                                        Web Development Course
                                                    </div>
                                                    <div className='text-label-sm-regular text-gray'>
                                                        Complete masterclass
                                                    </div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Sales Data',
                                            value: (
                                                <div className='space-y-1'>
                                                    <div className='text-label-md-medium text-success'>CA$299.99</div>
                                                    <div className='text-label-sm-regular text-gray'>Price</div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Performance',
                                            value: (
                                                <div className='space-y-1'>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Views:</span> 45,230
                                                    </div>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Conv:</span> 5.2%
                                                    </div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Status',
                                            type: 'pill',
                                            value: 'Active',
                                            statusVariant: 'active',
                                        },
                                        {
                                            name: 'Store URL',
                                            type: 'link',
                                            value: 'https://stan.store/web-dev-course',
                                            href: 'https://stan.store/web-dev-course',
                                            linkTarget: '_blank',
                                            showCopyButton: true,
                                        },
                                    ]}
                                    actionButtons={[
                                        {
                                            id: 'view-product',
                                            type: 'button',
                                            icon: FiEye,
                                            label: 'View Product',
                                            onClick: () => toast.info('Opening product page!'),
                                        },
                                    ]}
                                />

                                <StanDataRow
                                    columns={[
                                        {
                                            name: 'Product',
                                            icon: FaVideo,
                                            value: (
                                                <div>
                                                    <div className='text-label-md-medium text-dark'>
                                                        Design Templates
                                                    </div>
                                                    <div className='text-label-sm-regular text-gray'>Premium pack</div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Sales Data',
                                            value: (
                                                <div className='space-y-1'>
                                                    <div className='text-label-md-medium text-success'>CA$49.99</div>
                                                    <div className='text-label-sm-regular text-gray'>Price</div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Performance',
                                            value: (
                                                <div className='space-y-1'>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Views:</span> 23,450
                                                    </div>
                                                    <div className='text-label-sm-regular'>
                                                        <span className='text-gray'>Conv:</span> 8.1%
                                                    </div>
                                                </div>
                                            ),
                                        },
                                        {
                                            name: 'Status',
                                            type: 'pill',
                                            value: 'Draft',
                                            statusVariant: 'draft',
                                        },
                                        {
                                            name: 'Store URL',
                                            type: 'link',
                                            value: 'https://stan.store/design-templates',
                                            href: 'https://stan.store/design-templates',
                                            linkTarget: '_blank',
                                            showCopyButton: true,
                                        },
                                    ]}
                                />
                            </StanDataRowGroup>
                        ) : (
                            <CodeBlock
                                code={`import { StanDataRow, StanDataRowGroup } from '@/components';
import { FaVideo } from 'react-icons/fa';
import { FiEye } from 'react-icons/fi';

export const ProductsExample = () => {
  return (
    <StanDataRowGroup
      headerColumns={[
        { name: 'Product', width: 30 },
        { name: 'Sales Data', width: 20 },
        { name: 'Performance', width: 20 },
        { name: 'Status', width: 15 },
        { name: 'Store URL', width: 15 },
      ]}
      maxActionButtonCount={1}
    >
      <StanDataRow
        clickable
        onClick={() => handleClick()}
        columns={[
          {
            name: 'Product',
            icon: FaVideo,
            value: (
              <div>
                <div className="font-semibold">Web Development Course</div>
                <div className="text-sm text-gray-500">Complete masterclass</div>
              </div>
            ),
          },
          {
            name: 'Sales Data',
            value: (
              <div className="space-y-1">
                <div className="font-bold text-green-600">CA$299.99</div>
                <div className="text-sm text-gray-600">Price</div>
              </div>
            ),
          },
          {
            name: 'Performance',
            value: (
              <div className="space-y-1">
                <div className="text-sm">
                  <span className="text-gray-600">Views:</span> 45,230
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">Conv:</span> 5.2%
                </div>
              </div>
            ),
          },
          {
            name: 'Status',
            type: 'pill',
            value: 'Active',
            statusVariant: 'active',
          },
          {
            name: 'Store URL',
            type: 'link',
            value: 'https://stan.store/web-dev-course',
            href: 'https://stan.store/web-dev-course',
            linkTarget: '_blank',
            showCopyButton: true,
          },
        ]}
        actionButtons={[
          {
            id: 'view-product',
            type: 'button',
            icon: FiEye,
            label: 'View Product',
            onClick: () => handleView(),
          },
        ]}
      />
    </StanDataRowGroup>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* API Reference */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>API Reference</h2>
                </div>

                <div className='space-y-4'>
                    <div className='border-muted rounded-lg border p-6'>
                        <h3 className='text-heading-h3 text-dark mb-4'>StanDataRowGroup</h3>
                        <div className='space-y-3'>
                            <div className='flex items-start gap-4'>
                                <code className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-3 py-1.5 font-mono text-sm font-medium'>
                                    headerColumns
                                </code>
                                <span className='text-body-md text-gray pt-1'>
                                    Array of column definitions for the header
                                </span>
                            </div>
                            <div className='flex items-start gap-4'>
                                <code className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-3 py-1.5 font-mono text-sm font-medium'>
                                    maxActionButtonCount
                                </code>
                                <span className='text-body-md text-gray pt-1'>
                                    Maximum number of action buttons (optional)
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className='border-muted rounded-lg border p-6'>
                        <h3 className='text-heading-h3 text-dark mb-4'>StanDataRow</h3>
                        <div className='space-y-3'>
                            <div className='flex items-start gap-4'>
                                <code className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-3 py-1.5 font-mono text-sm font-medium'>
                                    columns
                                </code>
                                <span className='text-body-md text-gray pt-1'>Array of column data to display</span>
                            </div>
                            <div className='flex items-start gap-4'>
                                <code className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-3 py-1.5 font-mono text-sm font-medium'>
                                    actionButtons
                                </code>
                                <span className='text-body-md text-gray pt-1'>Array of action buttons (optional)</span>
                            </div>
                            <div className='flex items-start gap-4'>
                                <code className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-3 py-1.5 font-mono text-sm font-medium'>
                                    clickable
                                </code>
                                <span className='text-body-md text-gray pt-1'>
                                    Whether the row is clickable (optional)
                                </span>
                            </div>
                            <div className='flex items-start gap-4'>
                                <code className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-3 py-1.5 font-mono text-sm font-medium'>
                                    variant
                                </code>
                                <span className='text-body-md text-gray pt-1'>
                                    &apos;default&apos; | &apos;card&apos; | &apos;simple&apos; (optional)
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
